import React, { useEffect, useRef, useState } from "react";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { getContactNameDisplay, getContactPhoneDisplay } from "../../utils/contactPhoneDisplay";

// Autocomplete de contato reutilizável — usado tanto no modal de criação
// quanto no filtro da listagem. Busca debounced em GET /contacts, protegida
// contra resposta fora de ordem (requestId) e contra atualização de estado
// após desmontagem (isMounted). Nunca mostra número/LID cru: sempre passa
// pelo utilitário seguro de exibição já usado no restante do projeto.
const TaskContactAutocomplete = ({
  value,
  onChange,
  label,
  required = false,
  disabled = false,
  error = false,
  helperText,
  variant = "outlined",
  size = "small"
}) => {
  const [options, setOptions] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [loading, setLoading] = useState(false);

  const isMounted = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (disabled) {
      // Invalida também uma busca que já tenha saído antes de o campo ser
      // desabilitado (por exemplo, ao trocar CONTACT por INTERNAL).
      requestIdRef.current += 1;
      setLoading(false);
      setOptions([]);
      return undefined;
    }

    const currentRequestId = ++requestIdRef.current;
    setLoading(true);

    const delayDebounceFn = setTimeout(async () => {
      try {
        const { data } = await api.get("contacts", { params: { searchParam } });

        if (!isMounted.current || currentRequestId !== requestIdRef.current) return;
        setOptions(data.contacts || []);
      } catch (err) {
        if (!isMounted.current || currentRequestId !== requestIdRef.current) return;
        toastError(err);
      } finally {
        if (isMounted.current && currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, disabled]);

  return (
    <Autocomplete
      options={options}
      value={value || null}
      loading={loading}
      disabled={disabled}
      getOptionLabel={option => getContactNameDisplay(option)}
      getOptionSelected={(option, val) => option.id === val.id}
      noOptionsText={i18n.t("tasks.modal.noOptions")}
      onChange={(_, newValue) => onChange(newValue)}
      onInputChange={(_, newInputValue) => setSearchParam(newInputValue)}
      fullWidth
      renderOption={option => (
        <Typography variant="body2" noWrap>
          {getContactNameDisplay(option)} — {getContactPhoneDisplay(option, option.number)}
        </Typography>
      )}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={error}
          helperText={helperText}
          variant={variant}
          size={size}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
};

export default TaskContactAutocomplete;
